import React, { useEffect, useState } from 'react';
import { Card } from 'react-bootstrap';
import { toast } from 'react-toastify';
import {
  AdvanceTable,
  AdvanceTableHeader,
  AdvanceTablePagination,
  AdvanceTableWrapper
} from 'components/common/advance-table';
import SubsidyTransactionSearchPanel from 'components/common/search/SubsidyTransactionSearchPanel';
import { DATA_PER_PAGE, TABLE_COLUMN_FILTER_KEY } from 'constants';
import {
  LAST7DAYS_RANGE_TIME,
  getLastXdayUnix,
  todayUnix
} from 'components/common/CFDatePicker/config';
import SubsidyTransactionDetail from './SubsidyTransactionDetail';
import useFetchSubsidyTransactionData from './services/useFetchSubsidyTransactionData';
import useGetTableColumns from './services/useGetTableColumns';
import transactionServices from 'services/transaction.service';

const SubsidyTransactions = () => {
  const {
    subsidyTransactionList,
    subsidyTransactionDetail,
    loadingList,
    loadingDetail,
    fetchListSubsidyTransaction,
    fetchSubsidyTransactionDetail,
    resetSubsidyTransactionDetail
  } = useFetchSubsidyTransactionData();

  const [globalFilter, setGlobalFilter] = useState({
    dates: [getLastXdayUnix(6), todayUnix],
    rangeTime: LAST7DAYS_RANGE_TIME
  });
  const [selectedRow, setSelectedRow] = useState({});
  const [showDetail, setShowDetail] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const { visibleColumnIds, availableColumns, defaultVisibleColumnIds } = useGetTableColumns();

  const onClickRow = row => {
    fetchSubsidyTransactionDetail(row.id);
    setSelectedRow(row);
  };

  useEffect(() => {
    setShowDetail(!!selectedRow.id);
  }, [selectedRow.id]);

  const onCloseSubsidyTransactionDetail = () => {
    setShowDetail(false);
    setSelectedRow({});
    resetSubsidyTransactionDetail();
  };

  const handleChangeFilter = ({ rangeTime, dates, moreFilterData }) => {
    setGlobalFilter({ rangeTime, dates, moreFilterData });
  };

  const onExport = async (type, ext) => {
    try {
      setDownloading(true);
      const { dates = [], moreFilterData } = globalFilter;
      const exportParams = {
        start_time: dates[0],
        end_time: dates[1],
        ...moreFilterData
      };

      const respData = await transactionServices.exportSubsidies({ type, ...exportParams });
      if (!respData?.file_url) {
        toast.error('Could not download file. Please try later');
        return;
      }

      const link = document.createElement('a');
      link.href = respData.file_url;
      link.setAttribute('download', `list_subsidy_transaction_${Date.now()}.${ext}`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error(error);
    } finally {
      setDownloading(false);
    }
  };

  const { items = [], total_count: totalCount = 0 } = subsidyTransactionList || {};

  return (
    <>
      <AdvanceTableWrapper
        sortable
        pagination
        data={items}
        perPage={DATA_PER_PAGE}
        loading={loadingList}
        fetchData={fetchListSubsidyTransaction}
        hasDatePicker
        rowsPerPageSelection
        columns={availableColumns}
        initialDatePicker={globalFilter.dates}
        initialRangeTime={globalFilter.rangeTime}
        onChangeGlobalFilter={handleChangeFilter}
        totalCount={totalCount}
        visibleColumnIds={visibleColumnIds}
        defaultVisibleColumnIds={defaultVisibleColumnIds}
      >
        <div className="d-flex gap-1 bg-emphasis p-2 rounded-end-lg flex-column flex-md-row">
          <Card className="mb-3 mb-sm-0 w-100">
            <div className="flex-1" style={{ minHeight: 412 }}>
              <Card.Header>
                <AdvanceTableHeader
                  table
                  title="Subsidy Transactions"
                  canFilter
                  filterKey={TABLE_COLUMN_FILTER_KEY.SUBSIDY_TRANSACTION}
                  onExport={onExport}
                  downloading={downloading}
                  exportType={['excel', 'csv']}
                  canFilterColumns
                />
              </Card.Header>
              <Card.Body className="p-0 custom-table pb-md-3">
                <div className="flex-1 d-flex flex-column flex-1 scrollbar visible-scrollbar h-fit-content w-100 rounded-end-lg">
                  <AdvanceTable
                    table
                    headerClassName="bg-200 text-900 text-nowrap"
                    rowClassName="align-middle white-space-nowrap cursor-pointer"
                    onClickRow={onClickRow}
                    tableProps={{
                      size: 'sm',
                      striped: true,
                      className: 'fs--1 mb-0 overflow-hidden'
                    }}
                  />
                  <Card.Footer>
                    <AdvanceTablePagination table />
                  </Card.Footer>
                </div>
              </Card.Body>
            </div>
          </Card>
          <SubsidyTransactionSearchPanel table loadingList={loadingList} />
        </div>
      </AdvanceTableWrapper>
      <SubsidyTransactionDetail
        data={subsidyTransactionDetail}
        loading={loadingDetail}
        show={showDetail}
        onHide={onCloseSubsidyTransactionDetail}
      />
    </>
  );
};

export default SubsidyTransactions;
